import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Settings as SettingsIcon, Wine, Award, Workflow, Mail, Plus, Trash2, Save, X, Eye, EyeOff, Calendar, Languages, Share2, Linkedin, Facebook, Instagram, RefreshCw, Bell, Rocket } from "lucide-react";

const TABS = [
  { id: "general", label: "Général", icon: Calendar },
  { id: "social", label: "Réseaux sociaux", icon: Share2 },
  { id: "categories", label: "Catégories", icon: Award },
  { id: "poles", label: "Pôles", icon: Wine },
  { id: "projects", label: "Projets", icon: Rocket },
  { id: "fees", label: "Tarifs", icon: Wine },
  { id: "benefits", label: "Avantages", icon: SettingsIcon },
  { id: "workflow", label: "Workflow", icon: Workflow },
  { id: "event-packages", label: "Packages événements", icon: Calendar },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "templates", label: "Emails", icon: Mail },
  { id: "translations", label: "Traductions", icon: Languages },
];

export default function AdminSettings() {
  const [tab, setTab] = useState(() => localStorage.getItem("settings_tab") || "categories");
  const switchTab = (id) => { setTab(id); localStorage.setItem("settings_tab", id); };

  return (
    <PortalLayout sidebar="admin">
      <div>
        <h1 className="text-3xl font-bold text-navy md:text-4xl">Settings</h1>
        <p className="mt-1 text-neutral-600">Gérez l'ensemble de la configuration sans toucher au code : catégories, tarifs, avantages, workflow d'adhésion et emails.</p>
      </div>

      <div className="mt-6 inline-flex flex-wrap gap-1 rounded-full bg-wine-50/40 p-1" data-testid="settings-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => switchTab(t.id)}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold ${tab === t.id ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`}
            data-testid={`settings-tab-${t.id}`}
          >
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "general" && <GeneralPanel />}
        {tab === "social" && <SocialStatsPanel />}
        {tab === "categories" && <CategoriesPanel />}
        {tab === "poles" && <PolesPanel />}
        {tab === "projects" && <ProjectsPanel />}
        {tab === "fees" && <FeesPanel />}
        {tab === "benefits" && <BenefitsPanel />}
        {tab === "workflow" && <WorkflowPanel />}
        {tab === "event-packages" && <EventPackagesPanel />}
        {tab === "notifications" && <NotificationRoutesPanel />}
        {tab === "templates" && <EmailTemplatesPanel />}
        {tab === "translations" && <TranslationsPanel />}
      </div>
    </PortalLayout>
  );
}

/* ---------------- Categories ---------------- */
function CategoriesPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", order: 0, active: true, icon: "wine" });
  const [resetting, setResetting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationPlan, setMigrationPlan] = useState(null);

  const load = useCallback(() => api.get("/admin/settings/categories").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/settings/categories", form); toast.success("Catégorie créée"); setForm({ name: "", description: "", order: items.length + 1, active: true, icon: "wine" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const updateCat = async (c, patch) => { try { await api.patch(`/admin/settings/categories/${c.id}`, { ...c, ...patch }); load(); } catch { toast.error("Erreur"); } };
  const del = async (c) => { if (!window.confirm(`Supprimer "${c.name}" ?\n(Les tarifs et avantages liés seront aussi supprimés.)`)) return; await api.delete(`/admin/settings/categories/${c.id}`); load(); };
  const resetGrid = async () => {
    if (!window.confirm("⚠️ Cela va REMPLACER toutes les catégories et tarifs actuels par la grille officielle La WineTech (6 catégories).\n\nLes avantages personnalisés seront préservés.\n\nContinuer ?")) return;
    setResetting(true);
    try {
      const r = await api.post("/admin/settings/reset-membership-grid");
      toast.success(`Grille restaurée : ${r.data.categories_created} catégories, ${r.data.fees_created} tarifs`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setResetting(false); }
  };

  const previewMigration = async () => {
    setMigrating(true);
    try {
      const r = await api.post("/admin/settings/migrate-companies?dry_run=true");
      setMigrationPlan(r.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setMigrating(false); }
  };

  const applyMigration = async () => {
    if (!window.confirm(`Appliquer la migration sur ${migrationPlan.companies_affected} entreprise(s) ?\n\nLes pôles non reconnus seront vidés (à re-renseigner manuellement parmi les 5 nouveaux pôles).`)) return;
    setMigrating(true);
    try {
      const r = await api.post("/admin/settings/migrate-companies?dry_run=false");
      toast.success(`Migration appliquée sur ${r.data.companies_affected} entreprise(s)`);
      setMigrationPlan(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setMigrating(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="reset-grid-banner">
        <div className="text-xs text-amber-900">
          <strong>Grille officielle La WineTech</strong> — 6 catégories : Startups · Experts/Consultants · Producteurs · Comm. Équipement & Distribution · Instituts & Écoles · Partenaires.
        </div>
        <button
          onClick={resetGrid}
          disabled={resetting}
          className="inline-flex items-center gap-1.5 rounded-full border-2 border-amber-700 bg-white px-4 py-1.5 text-xs font-bold text-amber-900 hover:bg-amber-100 disabled:opacity-40"
          data-testid="reset-grid-btn"
        >
          {resetting ? "Restauration…" : "Restaurer la grille officielle"}
        </button>
      </div>

      <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4" data-testid="migrate-companies-banner">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-blue-900">
            <strong>Migrer les anciennes entreprises</strong> — Mappe les anciens libellés Pôle/Catégorie vers les nouveaux IDs (5 pôles & 6 catégories).
          </div>
          <button
            onClick={previewMigration}
            disabled={migrating}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-blue-700 bg-white px-4 py-1.5 text-xs font-bold text-blue-900 hover:bg-blue-100 disabled:opacity-40"
            data-testid="preview-migration-btn"
          >
            {migrating ? "Analyse…" : "Prévisualiser la migration"}
          </button>
        </div>

        {migrationPlan && (
          <div className="mt-4 space-y-2" data-testid="migration-plan">
            <div className="rounded-xl bg-white p-3">
              <div className="text-xs font-bold text-blue-900">{migrationPlan.companies_affected} entreprise(s) à migrer :</div>
              <div className="mt-2 space-y-2 text-xs">
                {migrationPlan.plan.map((p) => (
                  <div key={p.id} className="rounded-lg border border-neutral-200 p-2">
                    <div className="font-bold text-neutral-900">{p.name}</div>
                    <div className="mt-1 text-neutral-600">📊 Catégorie : <span className="font-semibold">{p.category.action}</span></div>
                    <div className="text-neutral-600">🍷 Pôle : <span className="font-semibold">{p.pole.action}</span></div>
                  </div>
                ))}
                {migrationPlan.plan.length === 0 && <div className="text-center text-neutral-500">✅ Toutes les entreprises sont déjà aux nouveaux IDs.</div>}
              </div>
            </div>
            {migrationPlan.companies_affected > 0 && (
              <button
                onClick={applyMigration}
                disabled={migrating}
                className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-5 py-2 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-40"
                data-testid="apply-migration-btn"
              >
                {migrating ? "Application…" : `Appliquer sur ${migrationPlan.companies_affected} entreprise(s)`}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
      <form onSubmit={save} className="space-y-3 rounded-2xl border border-wine-100 bg-white p-5">
        <h3 className="font-bold text-navy">Nouvelle catégorie</h3>
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom (ex: Startup & Innovation)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="cat-name" />
        <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description courte" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="cat-description" />
        <div className="grid grid-cols-2 gap-2">
          <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} placeholder="Ordre" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="cat-order" />
          <input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="Icône (wine, rocket…)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="cat-icon" />
        </div>
        <button type="submit" className="w-full rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="cat-submit"><Plus className="inline h-3 w-3" /> Créer</button>
      </form>
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.id} className="flex items-start gap-4 rounded-2xl border border-neutral-100 bg-white p-4" data-testid={`cat-row-${c.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <input value={c.name} onChange={(e) => updateCat(c, { name: e.target.value })} className="flex-1 rounded-lg border border-transparent bg-transparent px-2 py-1 text-base font-bold text-navy hover:border-neutral-200" data-testid={`cat-edit-name-${c.id}`} />
                <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] text-neutral-600">#{c.order}</span>
              </div>
              <textarea value={c.description || ""} onChange={(e) => updateCat(c, { description: e.target.value })} rows={2} className="mt-1 w-full rounded-lg border border-transparent bg-transparent px-2 py-1 text-xs text-neutral-600 hover:border-neutral-200" />
            </div>
            <div className="flex flex-col gap-1.5">
              <button onClick={() => updateCat(c, { active: !c.active })} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`} data-testid={`cat-active-${c.id}`}>
                {c.active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />} {c.active ? "Active" : "Inactive"}
              </button>
              <button onClick={() => del(c)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-700" title="Supprimer" data-testid={`cat-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="rounded-xl border border-dashed border-neutral-200 p-6 text-center text-sm text-neutral-500">Aucune catégorie. Créez la première.</div>}
      </div>
    </div>
    </div>
  );
}

/* ---------------- Fees ---------------- */
function FeesPanel() {
  const [cats, setCats] = useState([]);
  const [fees, setFees] = useState({});

  const load = useCallback(async () => {
    const [c, f] = await Promise.all([api.get("/admin/settings/categories"), api.get("/admin/settings/fees")]);
    setCats(c.data);
    const map = {}; f.data.forEach((x) => { map[x.category_id] = x; });
    setFees(map);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (cid, data) => {
    try { await api.put(`/admin/settings/fees/${cid}`, data); toast.success("Tarifs enregistrés"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      {cats.map((c) => <FeeEditor key={c.id} category={c} fee={fees[c.id]} onSave={(d) => save(c.id, d)} />)}
      {cats.length === 0 && <div className="rounded-2xl border border-dashed border-neutral-200 p-8 text-center text-sm text-neutral-500">Créez d'abord des catégories.</div>}
    </div>
  );
}

function FeeEditor({ category, fee, onSave }) {
  const [d, setD] = useState({
    bracket_1_label: "< 500 K€", bracket_2_label: "500 K€ - 2 M€", bracket_3_label: "2 M€ - 10 M€", bracket_4_label: "> 10 M€",
    threshold_1: 500000, threshold_2: 2000000, threshold_3: 10000000,
    fee_1: 0, fee_2: 0, fee_3: 0, fee_4: 0, currency: "eur", special_conditions: "", eligibility_rules: "",
  });
  useEffect(() => { if (fee) setD((prev) => ({ ...prev, ...fee })); }, [fee]);

  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid={`fee-block-${category.id}`}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-navy">{category.name}</h3>
        <button onClick={() => onSave(d)} className="inline-flex items-center gap-1 rounded-full bg-wine-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-wine-600" data-testid={`fee-save-${category.id}`}><Save className="h-3 w-3" /> Enregistrer</button>
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-xl border border-wine-100 bg-wine-50/30 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-wine-700">Tranche {i}</div>
            <input value={d[`bracket_${i}_label`]} onChange={(e) => setD({ ...d, [`bracket_${i}_label`]: e.target.value })} className="mt-2 w-full rounded-lg border border-neutral-200 bg-white px-2 py-1.5 text-xs font-semibold" placeholder="Libellé tranche" data-testid={`fee-label-${i}-${category.id}`} />
            {i < 4 && (
              <input type="number" value={d[`threshold_${i}`] || 0} onChange={(e) => setD({ ...d, [`threshold_${i}`]: parseFloat(e.target.value) || 0 })} className="mt-1.5 w-full rounded-lg border border-neutral-200 bg-white px-2 py-1.5 text-xs" placeholder="Seuil (€)" data-testid={`fee-threshold-${i}-${category.id}`} />
            )}
            <input type="number" value={d[`fee_${i}`]} onChange={(e) => setD({ ...d, [`fee_${i}`]: parseFloat(e.target.value) || 0 })} className="mt-1.5 w-full rounded-lg border border-wine-200 bg-white px-2 py-1.5 text-sm font-bold text-wine-700" placeholder="Tarif" data-testid={`fee-amount-${i}-${category.id}`} />
          </div>
        ))}
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <textarea rows={2} value={d.special_conditions} onChange={(e) => setD({ ...d, special_conditions: e.target.value })} placeholder="Conditions spéciales (ex: -50% 1ère année)" className="rounded-xl border border-neutral-200 px-3 py-2 text-xs" data-testid={`fee-special-${category.id}`} />
        <textarea rows={2} value={d.eligibility_rules} onChange={(e) => setD({ ...d, eligibility_rules: e.target.value })} placeholder="Règles d'éligibilité" className="rounded-xl border border-neutral-200 px-3 py-2 text-xs" data-testid={`fee-eligibility-${category.id}`} />
      </div>
    </div>
  );
}

/* ---------------- Benefits ---------------- */
function BenefitsPanel() {
  const [cats, setCats] = useState([]);
  const [map, setMap] = useState({});

  const load = useCallback(async () => {
    const [c, b] = await Promise.all([api.get("/admin/settings/categories"), api.get("/admin/settings/benefits")]);
    setCats(c.data);
    const m = {}; b.data.forEach((x) => { m[x.category_id] = x.items || []; });
    setMap(m);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (cid, items) => {
    try { await api.put(`/admin/settings/benefits/${cid}`, { category_id: cid, items }); toast.success("Avantages enregistrés"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      {cats.map((c) => <BenefitEditor key={c.id} category={c} items={map[c.id] || []} onSave={(items) => save(c.id, items)} />)}
    </div>
  );
}

function BenefitEditor({ category, items, onSave }) {
  const [list, setList] = useState(items);
  const [newItem, setNewItem] = useState("");
  useEffect(() => setList(items), [items]);
  const add = () => { if (newItem.trim()) { setList([...list, newItem.trim()]); setNewItem(""); } };
  const remove = (i) => setList(list.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid={`benefits-block-${category.id}`}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-navy">{category.name}</h3>
        <button onClick={() => onSave(list)} className="inline-flex items-center gap-1 rounded-full bg-wine-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-wine-600" data-testid={`benefits-save-${category.id}`}><Save className="h-3 w-3" /> Enregistrer</button>
      </div>
      <div className="space-y-2">
        {list.map((it, i) => (
          <div key={i} className="flex items-center gap-2 rounded-xl bg-wine-50/40 px-3 py-2">
            <span className="flex-1 text-sm text-neutral-800">{it}</span>
            <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700" data-testid={`benefits-del-${category.id}-${i}`}><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Nouvel avantage…" className="flex-1 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid={`benefits-input-${category.id}`} />
          <button onClick={add} className="rounded-full bg-wine-100 px-4 text-xs font-semibold text-wine-700 hover:bg-wine-200" data-testid={`benefits-add-${category.id}`}><Plus className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Workflow ---------------- */
function WorkflowPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ label: "", color: "bg-wine-50 text-wine-700", order: 0, terminal: false, active: true });
  const load = useCallback(() => api.get("/admin/settings/workflow").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => { e.preventDefault(); try { await api.post("/admin/settings/workflow", form); setForm({ label: "", color: "bg-wine-50 text-wine-700", order: items.length + 1, terminal: false, active: true }); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const upd = async (s, patch) => { try { await api.patch(`/admin/settings/workflow/${s.id}`, { ...s, ...patch }); load(); } catch { toast.error("Erreur"); } };
  const del = async (s) => { if (!window.confirm(`Supprimer "${s.label}" ?`)) return; await api.delete(`/admin/settings/workflow/${s.id}`); load(); };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
      <form onSubmit={save} className="space-y-3 rounded-2xl border border-wine-100 bg-white p-5">
        <h3 className="font-bold text-navy">Nouveau statut</h3>
        <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Libellé (ex: Validation comité)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="wf-label" />
        <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="Classes Tailwind couleur" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-xs font-mono" data-testid="wf-color" />
        <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} placeholder="Ordre" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="wf-order" />
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.terminal} onChange={(e) => setForm({ ...form, terminal: e.target.checked })} className="accent-wine-500" data-testid="wf-terminal" /> Statut terminal</label>
        <button type="submit" className="w-full rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="wf-submit"><Plus className="inline h-3 w-3" /> Créer</button>
      </form>
      <div className="space-y-2">
        {items.map((s) => (
          <div key={s.id} className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-white p-3" data-testid={`wf-row-${s.id}`}>
            <span className="text-xs font-bold text-neutral-400 w-6">#{s.order}</span>
            <span className={`inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${s.color}`}>{s.label}</span>
            {s.terminal && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Terminal</span>}
            <button onClick={() => upd(s, { active: !s.active })} className={`ml-auto rounded-full px-3 py-1 text-[10px] font-bold ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`} data-testid={`wf-active-${s.id}`}>{s.active ? "Active" : "Inactive"}</button>
            <button onClick={() => del(s)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-700" title="Supprimer" data-testid={`wf-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Email Templates ---------------- */
function EmailTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ slug: "", subject: "", body: "", lang: "fr", description: "", variables: [] });
  const [langFilter, setLangFilter] = useState("all");
  const load = useCallback(() => api.get("/admin/settings/email-templates").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const variables = [...new Set((form.body + " " + form.subject).match(/\{\{(\w+)\}\}/g)?.map((v) => v.replace(/[{}]/g, "")) || [])];
    try { await api.post("/admin/settings/email-templates", { ...form, variables }); toast.success("Template créé"); setForm({ slug: "", subject: "", body: "", lang: "fr", description: "", variables: [] }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const upd = async (t, patch) => {
    const merged = { ...t, ...patch };
    const variables = [...new Set((merged.body + " " + merged.subject).match(/\{\{(\w+)\}\}/g)?.map((v) => v.replace(/[{}]/g, "")) || [])];
    try { await api.patch(`/admin/settings/email-templates/${t.id}`, { ...merged, variables }); load(); } catch { toast.error("Erreur"); }
  };
  const del = async (t) => { if (!window.confirm(`Supprimer le template "${t.slug}" [${t.lang}] ?`)) return; await api.delete(`/admin/settings/email-templates/${t.id}`); load(); };

  const [autoTranslating, setAutoTranslating] = useState(null);
  const autoTranslate = async (t, missingLangs) => {
    if (!missingLangs || missingLangs.length === 0) { toast.info("Toutes les langues sont déjà couvertes"); return; }
    if (!window.confirm(`Auto-traduire « ${t.slug} » (source ${(t.lang || "fr").toUpperCase()}) vers ${missingLangs.map((l) => l.toUpperCase()).join(", ")} via Gemini ?\n\nLes placeholders {{variable}} seront préservés. Vous pourrez relire chaque traduction.`)) return;
    setAutoTranslating(t.id);
    try {
      const r = await api.post(`/admin/settings/email-templates/${t.id}/translate`, { target_langs: missingLangs });
      const created = r.data.created?.length || 0;
      const errors = r.data.errors || [];
      if (created > 0) toast.success(`${created} traduction${created > 1 ? "s" : ""} créée${created > 1 ? "s" : ""} ✨`);
      if (errors.length > 0) toast.error(`${errors.length} erreur(s) : ${errors.map((e) => e.lang).join(", ")}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur Gemini — vérifiez EMERGENT_LLM_KEY");
    } finally {
      setAutoTranslating(null);
    }
  };

  // Group by slug → list of existing langs; preload translate form
  const slugs = [...new Set(items.map((t) => t.slug))];
  const translate = (t, targetLang) => {
    setForm({
      slug: t.slug,
      subject: t.subject,
      body: t.body,
      lang: targetLang,
      description: t.description || "",
      variables: t.variables || [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const LANG_PILLS = [
    { id: "all", label: "Toutes" }, { id: "fr", label: "🇫🇷 FR" }, { id: "en", label: "🇬🇧 EN" },
    { id: "es", label: "🇪🇸 ES" }, { id: "it", label: "🇮🇹 IT" }, { id: "pt", label: "🇵🇹 PT" }, { id: "de", label: "🇩🇪 DE" },
  ];
  const ALL_LANGS = ["fr", "en", "es", "it", "pt", "de"];
  const filtered = langFilter === "all" ? items : items.filter((t) => t.lang === langFilter);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
      <form onSubmit={save} className="space-y-3 rounded-2xl border border-wine-100 bg-white p-5">
        <h3 className="font-bold text-navy">Nouveau template / Traduction</h3>
        <p className="text-xs text-neutral-500">Astuce&nbsp;: pour traduire un template existant, cliquez sur «&nbsp;+ Traduire&nbsp;» en face d&apos;une langue manquante — le formulaire sera pré-rempli.</p>
        <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.replace(/[^a-z0-9_]/g, "_") })} placeholder="Slug (ex: welcome_email)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" data-testid="tpl-slug" />
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description courte" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="tpl-description" />
        <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="tpl-lang">
          <option value="fr">🇫🇷 Français</option><option value="en">🇬🇧 English</option><option value="es">🇪🇸 Español</option><option value="it">🇮🇹 Italiano</option><option value="pt">🇵🇹 Português</option><option value="de">🇩🇪 Deutsch</option>
        </select>
        <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Sujet (variables {{name}} supportées)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="tpl-subject" />
        <textarea required rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Corps de l'email (Markdown, variables {{name}})" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" data-testid="tpl-body" />
        <button type="submit" className="w-full rounded-full bg-wine-500 px-5 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="tpl-submit"><Plus className="inline h-3 w-3" /> Enregistrer</button>
      </form>
      <div className="space-y-3">
        {/* Language filter */}
        <div className="flex flex-wrap gap-1.5">
          {LANG_PILLS.map((p) => (
            <button key={p.id} onClick={() => setLangFilter(p.id)} className={`rounded-full px-3 py-1 text-xs font-semibold ${langFilter === p.id ? "bg-wine-500 text-white" : "bg-white text-neutral-600 border border-neutral-200"}`} data-testid={`tpl-lang-filter-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Coverage matrix: shows which translations are missing for each slug */}
        {langFilter === "all" && slugs.length > 0 && (
          <div className="rounded-2xl border border-amber-100 bg-amber-50/50 p-4 text-xs">
            <h4 className="font-bold text-amber-900">Couverture multilingue</h4>
            <div className="mt-2 space-y-1.5">
              {slugs.map((slug) => {
                const langsForSlug = items.filter((t) => t.slug === slug).map((t) => t.lang);
                const missing = ALL_LANGS.filter((l) => !langsForSlug.includes(l));
                const base = items.find((t) => t.slug === slug && t.lang === "fr") || items.find((t) => t.slug === slug);
                return (
                  <div key={slug} className="flex flex-wrap items-center gap-1.5">
                    <code className="rounded bg-white px-2 py-0.5 font-mono">{slug}</code>
                    {ALL_LANGS.map((l) => (
                      <span key={l} className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${langsForSlug.includes(l) ? "bg-emerald-100 text-emerald-700" : "bg-red-50 text-red-600"}`}>{l.toUpperCase()}</span>
                    ))}
                    {missing.length > 0 && base && missing.map((l) => (
                      <button key={l} onClick={() => translate(base, l)} className="rounded-full bg-wine-500 px-2 py-0.5 text-[10px] font-bold text-white hover:bg-wine-600" data-testid={`tpl-translate-${slug}-${l}`}>+ Traduire en {l.toUpperCase()}</button>
                    ))}
                    {missing.length > 0 && base && (
                      <button
                        onClick={() => autoTranslate(base, missing)}
                        disabled={autoTranslating === base.id}
                        className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-purple-500 to-fuchsia-500 px-2.5 py-0.5 text-[10px] font-bold text-white hover:from-purple-600 hover:to-fuchsia-600 disabled:opacity-60"
                        data-testid={`tpl-auto-translate-${slug}`}
                        title={`Traduire automatiquement vers ${missing.map((l) => l.toUpperCase()).join(", ")} via Gemini`}
                      >
                        {autoTranslating === base.id ? "⏳ Gemini…" : `✨ Auto-traduire (${missing.length})`}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {filtered.map((t) => (
          <details key={t.id} className="rounded-2xl border border-neutral-100 bg-white" data-testid={`tpl-row-${t.id}`}>
            <summary className="flex cursor-pointer items-center gap-3 p-4">
              <span className="rounded-full bg-wine-50 px-2 py-0.5 font-mono text-xs font-bold text-wine-700">{t.slug}</span>
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase text-neutral-600">{t.lang}</span>
              <span className="flex-1 truncate text-sm font-semibold text-neutral-800">{t.subject}</span>
              <button onClick={(e) => { e.preventDefault(); del(t); }} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-700" title="Supprimer" data-testid={`tpl-del-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </summary>
            <div className="space-y-2 border-t border-neutral-100 p-4">
              <input value={t.subject} onChange={(e) => upd(t, { subject: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <textarea rows={6} value={t.body} onChange={(e) => upd(t, { body: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" />
              {t.variables?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {t.variables.map((v) => <span key={v} className="rounded-full bg-wine-50 px-2 py-0.5 font-mono text-[10px] text-wine-700">{`{{${v}}}`}</span>)}
                </div>
              )}
            </div>
          </details>
        ))}
        {filtered.length === 0 && <div className="rounded-xl border border-dashed border-neutral-200 p-6 text-center text-sm text-neutral-500">Aucun template.</div>}
      </div>
    </div>
  );
}


/* ---------------- Poles ---------------- */
function PolesPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", emoji: "", order: 0, active: true });

  const load = useCallback(() => api.get("/admin/settings/poles").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    try {
      await api.post("/admin/settings/poles", { ...form, order: form.order || items.length + 1 });
      toast.success("Pôle créé");
      setForm({ name: "", description: "", emoji: "", order: 0, active: true });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const updatePole = async (p, patch) => {
    try { await api.patch(`/admin/settings/poles/${p.id}`, { ...p, ...patch }); load(); }
    catch { toast.error("Erreur"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Supprimer le pôle "${p.name}" ?\nLes membres associés conserveront l'identifiant mais ne seront plus catégorisés sur le tableau de bord.`)) return;
    await api.delete(`/admin/settings/poles/${p.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="poles-panel">
      <div className="rounded-2xl border border-neutral-100 bg-white p-6">
        <h2 className="text-lg font-bold text-navy">Pôles sectoriels</h2>
        <p className="mt-1 text-xs text-neutral-500">Définissez les pôles d'activité. Ils sont affichés sur le Tableau de bord (« Membres par pôle ») et utilisés pour catégoriser les entreprises.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold text-neutral-600">Nom *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex: Producteurs" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="pole-form-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600">Emoji</label>
            <input value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} placeholder="🍷" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="pole-form-emoji" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-neutral-600">Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Domaines, caves, négoce…" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="pole-form-desc" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600">Ordre</label>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="pole-form-order" />
          </div>
          <div className="flex items-end">
            <button onClick={create} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="pole-form-create"><Plus className="h-4 w-4" /> Créer le pôle</button>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <div className="rounded-xl border border-dashed border-neutral-200 p-6 text-center text-sm text-neutral-500">Aucun pôle. Créez le premier ci-dessus.</div>}
        {items.map((p) => (
          <div key={p.id} className="flex items-center justify-between rounded-xl border border-neutral-100 bg-white p-4" data-testid={`pole-row-${p.id}`}>
            <div className="flex items-center gap-3">
              <span className="text-xl">{p.emoji || "•"}</span>
              <div>
                <div className="text-sm font-semibold text-neutral-900">{p.name} <span className="text-xs font-normal text-neutral-400">#{p.id}</span></div>
                {p.description && <div className="text-xs text-neutral-500">{p.description}</div>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => updatePole(p, { active: !p.active })} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-600"}`} data-testid={`pole-toggle-${p.id}`}>
                {p.active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                {p.active ? "Actif" : "Masqué"}
              </button>
              <button onClick={() => del(p)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-700" title="Supprimer" data-testid={`pole-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GeneralPanel() {
  const [calendarUrl, setCalendarUrl] = useState("");
  const [activationCalendarUrl, setActivationCalendarUrl] = useState("");
  const [notificationEmail, setNotificationEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingActivation, setSavingActivation] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);
  // EtOH Studio defaults
  const [etohDefaults, setEtohDefaults] = useState({ workspace_id: "", content_category_id: "" });
  const [etohWorkspaces, setEtohWorkspaces] = useState([]);
  const [etohCategories, setEtohCategories] = useState([]);
  const [etohSaving, setEtohSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/public/apply-config").catch(() => ({ data: {} })),
      api.get("/admin/settings/notification-email").catch(() => ({ data: {} })),
      api.get("/admin/contentstudio/defaults").catch(() => ({ data: {} })),
      api.get("/admin/contentstudio/workspaces").catch(() => ({ data: { items: [] } })),
      api.get("/admin/settings/activation-calendar-url").catch(() => ({ data: {} })),
    ]).then(([cfg, notif, def, ws, activ]) => {
      setCalendarUrl(cfg.data?.calendar_url || "");
      setActivationCalendarUrl(activ.data?.calendar_url || "");
      setNotificationEmail(notif.data?.notification_email || "");
      setEtohDefaults(def.data || { workspace_id: "", content_category_id: "" });
      setEtohWorkspaces(ws.data?.items || []);
      const initWs = def.data?.workspace_id || ws.data?.items?.[0]?.id;
      if (initWs) {
        api.get(`/admin/contentstudio/workspaces/${initWs}/content-categories`)
          .then((r) => setEtohCategories(r.data?.items || []))
          .catch(() => setEtohCategories([]));
      }
      setLoading(false);
    });
  }, []);

  const changeEtohWorkspace = (wsId) => {
    setEtohDefaults((d) => ({ ...d, workspace_id: wsId, content_category_id: "" }));
    setEtohCategories([]);
    if (!wsId) return;
    api.get(`/admin/contentstudio/workspaces/${wsId}/content-categories`)
      .then((r) => setEtohCategories(r.data?.items || []))
      .catch(() => setEtohCategories([]));
  };

  const saveEtohDefaults = async () => {
    setEtohSaving(true);
    try {
      await api.patch("/admin/contentstudio/defaults", etohDefaults);
      toast.success("Réglages EtOH Studio enregistrés");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setEtohSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings/apply-calendar-url", { calendar_url: calendarUrl.trim() });
      toast.success("Lien calendrier mis à jour");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const saveActivation = async () => {
    setSavingActivation(true);
    try {
      await api.patch("/admin/settings/activation-calendar-url", { calendar_url: activationCalendarUrl.trim() });
      toast.success("Lien d'activation mis à jour");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSavingActivation(false);
    }
  };

  const saveNotif = async () => {
    setSavingNotif(true);
    try {
      await api.patch("/admin/settings/notification-email", { notification_email: notificationEmail.trim() });
      toast.success("Email de notification mis à jour");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSavingNotif(false);
    }
  };

  if (loading) return <div className="text-sm text-neutral-500">Chargement…</div>;

  return (
    <div className="space-y-6">
      {/* Sprint 88 — Highly visible admin maintenance action (moved from CRM Digest tab) */}
      <MemberRoleReconcileBlock />

      <div className="rounded-2xl border border-wine-100 bg-white p-6 shadow-sm" data-testid="general-panel-calendar">
        <div className="flex items-start gap-3">
          <Calendar className="mt-1 h-5 w-5 text-wine-600" />
          <div className="flex-1">
            <h3 className="text-lg font-bold text-navy">Lien calendrier — RDV de candidature</h3>
            <p className="mt-1 text-sm text-neutral-600">
              URL du calendrier (Google Calendar appointment schedule, Calendly, Cal.com…) affichée sur la page de remerciement après envoi de la candidature.
              Les candidats pourront y réserver directement un créneau de 30 min.
            </p>
            <div className="mt-4 space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">URL complète</label>
              <input
                value={calendarUrl}
                onChange={(e) => setCalendarUrl(e.target.value)}
                placeholder="https://calendar.google.com/calendar/u/0/appointments/AcZss…"
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                data-testid="apply-calendar-input"
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="apply-calendar-save">
                <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
              {calendarUrl && (
                <a href={calendarUrl} target="_blank" rel="noreferrer" className="text-xs font-semibold text-wine-700 hover:underline">
                  Tester le lien ↗
                </a>
              )}
            </div>
            <p className="mt-3 text-[11px] text-neutral-500">
              💡 Pour générer un lien Google Calendar : Google Calendar → Créer un planning de rendez-vous → Copier le lien public de la page de réservation.
            </p>
          </div>
        </div>
      </div>

      {/* Sprint 87 — Activation calendar (post-payment booking with Audrey) */}
      <div className="rounded-2xl border border-emerald-200 bg-white p-6 shadow-sm" data-testid="general-panel-activation-calendar">
        <div className="flex items-start gap-3">
          <Calendar className="mt-1 h-5 w-5 text-emerald-600" />
          <div className="flex-1">
            <h3 className="text-lg font-bold text-navy">🥂 Lien calendrier — Activation après paiement</h3>
            <p className="mt-1 text-sm text-neutral-600">
              URL envoyée automatiquement au nouveau membre <strong>dès la réception de son paiement Stripe</strong>.
              Le candidat reçoit un email « Activez votre adhésion en prenant RDV avec Audrey » avec ce lien pour planifier
              l&apos;échange d&apos;onboarding personnalisé.
            </p>
            <div className="mt-4 space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">URL complète</label>
              <input
                value={activationCalendarUrl}
                onChange={(e) => setActivationCalendarUrl(e.target.value)}
                placeholder="https://calendar.app.google/Us9qKEy92JDb5dRx5"
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                data-testid="activation-calendar-input"
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button onClick={saveActivation} disabled={savingActivation} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-5 py-2 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-50" data-testid="activation-calendar-save">
                <Save className="h-4 w-4" /> {savingActivation ? "Enregistrement…" : "Enregistrer"}
              </button>
              {activationCalendarUrl && (
                <a href={activationCalendarUrl} target="_blank" rel="noreferrer" className="text-xs font-semibold text-emerald-700 hover:underline">
                  Tester le lien ↗
                </a>
              )}
            </div>
            <p className="mt-3 text-[11px] text-neutral-500">
              💡 Par défaut, le lien public d&apos;Audrey : <code className="rounded bg-neutral-100 px-1 text-[11px]">https://calendar.app.google/Us9qKEy92JDb5dRx5</code>
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-wine-100 bg-white p-6 shadow-sm" data-testid="general-panel-notification-email">
        <div className="flex items-start gap-3">
          <Mail className="mt-1 h-5 w-5 text-wine-600" />
          <div className="flex-1">
            <h3 className="text-lg font-bold text-navy">Emails de notification — Toutes les alertes admin</h3>
            <p className="mt-1 text-sm text-neutral-600">
              Adresses qui reçoivent les emails récapitulatifs pour <strong>toutes les actions sensibles</strong> :
              nouvelles candidatures, formulaires de contact, soumissions Fillout, signatures de pétitions, etc.
              <br />
              Vous pouvez saisir <strong>plusieurs adresses séparées par une virgule</strong>.
              Par défaut : <code className="rounded bg-neutral-100 px-1 text-[11px]">audrey@lawinetech.com</code>.
            </p>
            <div className="mt-4 space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Destinataires (séparés par des virgules)</label>
              <textarea
                rows={2}
                value={notificationEmail}
                onChange={(e) => setNotificationEmail(e.target.value)}
                placeholder="audrey@lawinetech.com, contact@lawinetech.com"
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono"
                data-testid="notification-email-input"
              />
            </div>
            <div className="mt-4">
              <button onClick={saveNotif} disabled={savingNotif} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="notification-email-save">
                <Save className="h-4 w-4" /> {savingNotif ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-100 bg-amber-50/40 p-6 text-sm" data-testid="general-panel-info">
        <h3 className="font-bold text-amber-900">ℹ️ Autres paramètres à venir</h3>
        <p className="mt-2 text-amber-800">
          D&apos;autres réglages globaux (logo, bannière event, signature email, etc.) seront disponibles dans cet onglet au fil des versions.
        </p>
      </div>

      <div className="rounded-2xl border border-wine-100 bg-white p-6 shadow-sm" data-testid="general-panel-etoh-studio">
        <div className="flex items-start gap-3">
          <SettingsIcon className="mt-1 h-5 w-5 text-wine-600" />
          <div className="flex-1">
            <h3 className="text-lg font-bold text-navy">EtOH Studio — défauts de publication</h3>
            <p className="mt-1 text-sm text-neutral-600">
              Code client (workspace) et content category sélectionnés par défaut lors de la publication d&apos;un visuel social. L&apos;admin peut toujours les changer au cas par cas.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Code client (workspace)</label>
                <select value={etohDefaults.workspace_id || ""} onChange={(e) => changeEtohWorkspace(e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="etoh-default-workspace">
                  <option value="">— Aucun —</option>
                  {etohWorkspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Content category par défaut</label>
                <select value={etohDefaults.content_category_id || ""} onChange={(e) => setEtohDefaults({ ...etohDefaults, content_category_id: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="etoh-default-category">
                  <option value="">— Aucune —</option>
                  {etohCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <button onClick={saveEtohDefaults} disabled={etohSaving} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="etoh-default-save">
              <Save className="h-4 w-4" /> {etohSaving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <p className="mt-3 text-[11px] text-neutral-500">
              💡 La clé API EtOH Studio est définie dans la variable d&apos;environnement <code className="rounded bg-neutral-100 px-1 py-0.5 text-[10px]">CONTENTSTUDIO_API_KEY</code> côté backend.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}



/* ---------------- Translations ---------------- */
const LANG_LABELS = { en: "Anglais", es: "Espagnol", it: "Italien", pt: "Portugais", de: "Allemand" };
const COLLECTION_LABELS = {
  news: "Actualités",
  events: "Événements",
  companies: "Entreprises",
  membership_categories: "Catégories d'adhésion",
  resources: "Ressources",
};

function TranslationsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyLang, setBusyLang] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/translations/coverage");
      setData(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Impossible de charger la couverture");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const prewarm = async (lang, force = false) => {
    if (busyLang) return;
    setBusyLang(lang);
    const t = toast.loading(`Pré-traduction ${LANG_LABELS[lang]} en cours…`);
    try {
      const r = await api.post("/admin/translations/prewarm", { langs: [lang], force });
      toast.dismiss(t);
      toast.success(
        `${LANG_LABELS[lang]} : ${r.data.translations} nouvelles traductions, ${r.data.skipped} déjà en cache (${r.data.elapsed_seconds}s)`
      );
      await load();
    } catch (err) {
      toast.dismiss(t);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur pendant la pré-traduction");
    } finally {
      setBusyLang(null);
    }
  };

  const prewarmAll = async () => {
    if (busyLang) return;
    if (!window.confirm("Pré-traduire TOUT le contenu manquant dans les 5 langues ?\n\nCela peut prendre plusieurs minutes selon le volume de contenu et consomme du crédit LLM.")) return;
    setBusyLang("all");
    const t = toast.loading("Pré-traduction de toutes les langues en cours…");
    try {
      const r = await api.post("/admin/translations/prewarm", {});
      toast.dismiss(t);
      toast.success(`${r.data.translations} nouvelles traductions générées en ${r.data.elapsed_seconds}s`);
      await load();
    } catch (err) {
      toast.dismiss(t);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setBusyLang(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 text-sm text-neutral-500" data-testid="translations-loading">
        Chargement de la couverture de traduction…
      </div>
    );
  }

  const langs = data.langs || [];
  const summary = data.summary || { total_docs: 0, pct: {}, translated: {} };

  return (
    <div className="space-y-6" data-testid="translations-panel">
      {/* Header */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-navy">Couverture de traduction</h2>
            <p className="mt-1 text-sm text-neutral-600">
              Pourcentage de documents publics dont le contenu est déjà traduit (mis en cache).
              Les visiteurs dans une langue couverte évitent l&apos;appel LLM et reçoivent la traduction en ~5ms.
            </p>
          </div>
          <button
            onClick={prewarmAll}
            disabled={!!busyLang}
            className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50"
            data-testid="prewarm-all-btn"
          >
            <Languages className="h-4 w-4" />
            {busyLang === "all" ? "Pré-traduction en cours…" : "Pré-traduire tout"}
          </button>
        </div>

        {/* Global summary */}
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {langs.map((lang) => {
            const pct = summary.pct?.[lang] ?? 0;
            const translated = summary.translated?.[lang] ?? 0;
            const total = summary.total_docs ?? 0;
            return (
              <div key={lang} className="rounded-xl border border-neutral-200 bg-wine-50/30 p-4" data-testid={`coverage-summary-${lang}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{LANG_LABELS[lang] || lang}</span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-600">{lang.toUpperCase()}</span>
                </div>
                <div className="mt-2 text-2xl font-bold text-navy">{pct}%</div>
                <div className="text-xs text-neutral-500">{translated} / {total} documents</div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-neutral-200">
                  <div
                    className={`h-full ${pct >= 95 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-rose-500"}`}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
                <button
                  onClick={() => prewarm(lang)}
                  disabled={!!busyLang}
                  className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-full border border-wine-300 bg-white px-3 py-1.5 text-xs font-semibold text-wine-700 hover:bg-wine-50 disabled:opacity-50"
                  data-testid={`prewarm-btn-${lang}`}
                >
                  {busyLang === lang ? "En cours…" : `Pré-traduire ${LANG_LABELS[lang]}`}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-collection breakdown */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid="coverage-breakdown">
        <h3 className="text-base font-bold text-navy">Détail par collection</h3>
        <p className="mt-1 text-xs text-neutral-500">Couverture détaillée par type de contenu et par langue.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left">
                <th className="py-2 pr-3 text-xs font-semibold uppercase tracking-wide text-neutral-500">Collection</th>
                <th className="py-2 pr-3 text-xs font-semibold uppercase tracking-wide text-neutral-500">Docs</th>
                {langs.map((l) => (
                  <th key={l} className="py-2 pr-3 text-xs font-semibold uppercase tracking-wide text-neutral-500">{l.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.collections || {}).map(([name, stats]) => (
                <tr key={name} className="border-b border-neutral-100" data-testid={`coverage-row-${name}`}>
                  <td className="py-2 pr-3 font-semibold text-neutral-800">{COLLECTION_LABELS[name] || name}</td>
                  <td className="py-2 pr-3 text-neutral-600">{stats.total}</td>
                  {langs.map((l) => {
                    const pct = stats.pct?.[l] ?? 0;
                    const tr = stats.translated?.[l] ?? 0;
                    return (
                      <td key={l} className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold ${pct >= 95 ? "text-emerald-600" : pct >= 60 ? "text-amber-600" : "text-rose-600"}`}>
                            {pct}%
                          </span>
                          <span className="text-[11px] text-neutral-400">({tr}/{stats.total})</span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-[11px] text-neutral-500">
          💡 Une couverture verte (≥95%) signifie que la quasi-totalité des visiteurs de cette langue obtient une traduction instantanée.
          Les valeurs en jaune/rouge déclencheront un appel LLM à la volée la première fois qu&apos;un visiteur consulte ce contenu.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Sprint 73 — Social Stats Panel : LinkedIn / Facebook / Instagram followers
// ============================================================================
function SocialStatsPanel() {
  const [v, setV] = useState({ linkedin: 0, facebook: 0, instagram: 0, linkedin_url: "", facebook_url: "", instagram_url: "", source: "manual", last_updated: null });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/social-stats"); setV({ ...v, ...(r.data || {}) }); }
    catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/social-stats", v);
      toast.success("Statistiques sociales enregistrées");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };

  const [csNote, setCsNote] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);

  const sendReminder = async () => {
    if (!window.confirm("Envoyer un email de rappel à audrey@lawinetech.com maintenant ?")) return;
    setSendingReminder(true);
    try {
      await api.post("/admin/social-stats/reminder-tick", {});
      toast.success("Email de rappel envoyé à audrey@lawinetech.com");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSendingReminder(false); }
  };

  const syncCS = async () => {
    if (!window.confirm("Synchroniser les compteurs depuis ContentStudio ?\n\nNB : L'API publique de ContentStudio ne fournit pas les nombres d'abonnés — vos comptes seront détectés mais vous devrez saisir les compteurs manuellement.")) return;
    setSyncing(true);
    setCsNote(null);
    try {
      const r = await api.post("/admin/social-stats/sync-contentstudio");
      setV(r.data.value || v);
      if (r.data.note) {
        setCsNote({ note: r.data.note, detected: r.data.detected_accounts });
        toast("Comptes ContentStudio détectés — saisissez les compteurs ci-dessous.", { icon: "ℹ️" });
      } else {
        toast.success("Synchronisation ContentStudio terminée");
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur de synchronisation"); }
    finally { setSyncing(false); }
  };

  const Card = ({ icon: Icon, label, color, num, urlKey, numKey }) => (
    <div className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid={`social-card-${label.toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className={`grid h-10 w-10 place-items-center rounded-xl ${color} text-white`}><Icon className="h-5 w-5" /></div>
        <div className="font-semibold text-navy">{label}</div>
      </div>
      <label className="mt-4 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Nombre d'abonnés</label>
      <input type="number" min="0" value={num} onChange={(e) => setV({ ...v, [numKey]: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-2xl font-bold text-navy" data-testid={`social-input-${label.toLowerCase()}`} />
      <label className="mt-3 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">URL publique</label>
      <input type="url" value={v[urlKey] || ""} onChange={(e) => setV({ ...v, [urlKey]: e.target.value })} placeholder="https://..." className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid={`social-url-${label.toLowerCase()}`} />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="social-stats-panel">
      <div className="rounded-2xl border border-wine-100 bg-wine-50/30 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-wine-700">📡 Réseaux sociaux</div>
            <h3 className="mt-1 text-lg font-bold text-navy">Compteurs « La WineTech rayonne »</h3>
            <p className="mt-1 text-xs text-neutral-600">Ces chiffres apparaissent sur la page d'accueil publique. Synchronisez depuis ContentStudio ou saisissez les valeurs manuellement.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {v.last_updated && (
              <div className="text-[11px] text-neutral-500">
                Dernière maj : {new Date(v.last_updated).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                <span className="ml-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-wine-700">{v.source}</span>
              </div>
            )}
            <button onClick={syncCS} disabled={syncing} className="inline-flex items-center gap-1.5 rounded-full border border-wine-300 bg-white px-4 py-2 text-xs font-bold text-wine-700 hover:bg-wine-50 disabled:opacity-50" data-testid="sync-contentstudio-btn">
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sync…" : "Sync ContentStudio"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card icon={Linkedin} label="LinkedIn" color="bg-[#0A66C2]" num={v.linkedin} urlKey="linkedin_url" numKey="linkedin" />
        <Card icon={Facebook} label="Facebook" color="bg-[#1877F2]" num={v.facebook} urlKey="facebook_url" numKey="facebook" />
        <Card icon={Instagram} label="Instagram" color="bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]" num={v.instagram} urlKey="instagram_url" numKey="instagram" />
      </div>

      {csNote && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900" data-testid="cs-sync-note">
          <div className="font-bold inline-flex items-center gap-1.5">ℹ️ Comptes ContentStudio détectés</div>
          <p className="mt-1 text-xs text-amber-800">{csNote.note}</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {["linkedin","facebook","instagram"].map((p) => {
              const accs = (csNote.detected || {})[p] || [];
              return (
                <div key={p} className="rounded-xl bg-white/70 px-3 py-2 text-xs">
                  <div className="font-bold uppercase tracking-wider text-amber-700">{p}</div>
                  {accs.length === 0 ? <div className="italic text-amber-600">non connecté</div> :
                    accs.map((a) => <div key={a.id} className="truncate">✓ {a.name}</div>)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={sendReminder} disabled={sendingReminder} className="inline-flex items-center gap-2 rounded-full border border-wine-300 bg-white px-5 py-3 text-xs font-bold text-wine-700 hover:bg-wine-50 disabled:opacity-50" data-testid="social-stats-send-reminder">
          📧 {sendingReminder ? "Envoi…" : "Tester rappel à Audrey"}
        </button>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-6 py-3 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="social-stats-save">
          <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>

      {/* History timeline */}
      <SocialStatsHistory refresher={v.last_updated} />
    </div>
  );
}

function DeltaBadge({ n }) {
  if (!n) return <span className="text-neutral-300">±0</span>;
  return <span className={`ml-1 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${n > 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{n > 0 ? "+" : ""}{n}</span>;
}

function SocialStatsHistory({ refresher }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/admin/social-stats/history?limit=20")
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refresher]);

  if (loading) return <div className="text-xs text-neutral-400 italic">Chargement de l&apos;historique…</div>;
  if (items.length === 0) return <div className="rounded-xl border border-dashed border-neutral-200 p-4 text-center text-xs text-neutral-400">Aucun historique pour le moment — enregistrez les compteurs pour démarrer le suivi.</div>;

  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid="social-stats-history">
      <h3 className="text-base font-bold text-navy inline-flex items-center gap-2">📈 Historique des évolutions</h3>
      <p className="mt-1 text-xs text-neutral-500">Suivez l&apos;évolution semaine après semaine. Les variations sont calculées automatiquement entre deux saisies consécutives.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-wine-50/40 text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-right">LinkedIn</th>
              <th className="px-3 py-2 text-right">Facebook</th>
              <th className="px-3 py-2 text-right">Instagram</th>
              <th className="px-3 py-2 text-right">Newsletter</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-neutral-100">
                <td className="px-3 py-2 text-neutral-500">{(it.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2"><span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold">{it.source}</span></td>
                <td className="px-3 py-2 text-right font-semibold text-navy">{it.linkedin}<DeltaBadge n={it.delta?.linkedin} /></td>
                <td className="px-3 py-2 text-right font-semibold text-navy">{it.facebook}<DeltaBadge n={it.delta?.facebook} /></td>
                <td className="px-3 py-2 text-right font-semibold text-navy">{it.instagram}<DeltaBadge n={it.delta?.instagram} /></td>
                <td className="px-3 py-2 text-right font-semibold text-navy">{it.newsletter}<DeltaBadge n={it.delta?.newsletter} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Notification Routing Panel ---------------- */

/* ---------------- Event Packages Panel ---------------- */
function EventPackagesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const empty = () => ({ title: "", description: "", price_eur: 4900, duration_days: 30, features: [], is_active: true, sort_order: (items.length || 0) + 1 });
  const [form, setForm] = useState(empty());

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/event-packages").then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty(), ...p, features: p.features || [] }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Titre obligatoire");
    try {
      if (editing) await api.patch(`/admin/event-packages/${editing.id}`, form);
      else await api.post("/admin/event-packages", form);
      toast.success(editing ? "Package mis à jour" : "Package créé");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Supprimer le package "${p.title}" ?`)) return;
    try { await api.delete(`/admin/event-packages/${p.id}`); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-neutral-100 bg-white p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-navy"><Calendar className="mr-1 inline h-5 w-5 text-wine-500" /> Packages événements (paiement Stripe)</h2>
            <p className="mt-1 text-sm text-neutral-600">Configurez les formules payantes proposées aux non-membres pour publier un événement sur La WineTech.</p>
          </div>
          <button onClick={openNew} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="event-package-new"><Plus className="h-4 w-4" /> Nouveau package</button>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead className="bg-wine-50/40 text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-3 py-2 text-left">Ordre</th>
                <th className="px-3 py-2 text-left">Titre</th>
                <th className="px-3 py-2 text-right">Prix</th>
                <th className="px-3 py-2 text-right">Durée</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-3 py-6 text-center text-neutral-400"><RefreshCw className="inline h-4 w-4 animate-spin" /></td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-neutral-400">Aucun package. <button onClick={openNew} className="text-wine-700 font-bold hover:underline">Créer le premier</button></td></tr>}
              {items.map((p) => (
                <tr key={p.id} className="border-t border-neutral-100">
                  <td className="px-3 py-2 text-neutral-500">{p.sort_order}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-navy">{p.title}</div>
                    <div className="text-xs text-neutral-500">{p.description}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-wine-700">{(p.price_eur / 100).toFixed(0)}€</td>
                  <td className="px-3 py-2 text-right">{p.duration_days} jours</td>
                  <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.is_active ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>{p.is_active ? "Actif" : "Inactif"}</span></td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openEdit(p)} className="mr-1 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-[10px] font-bold text-blue-700 hover:bg-blue-100"><Edit3Icon /> Éditer</button>
                    <button onClick={() => remove(p)} className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-1 text-[10px] font-bold text-rose-700 hover:bg-rose-100"><Trash2 className="h-3 w-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="event-package-modal">
            <h3 className="text-xl font-bold text-navy">{editing ? "Modifier le package" : "Nouveau package"}</h3>
            <div className="mt-4 space-y-3">
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="pkg-title-input" />
              <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description courte" className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" />
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Prix (centimes EUR)</label>
                  <input type="number" min={0} value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: parseInt(e.target.value || 0) })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="pkg-price-input" />
                  <span className="text-[10px] text-neutral-400">= {(form.price_eur / 100).toFixed(2)}€</span>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Durée (jours)</label>
                  <input type="number" min={1} value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value || 1) })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Ordre</label>
                  <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || 0) })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Points clés (une ligne par avantage)</label>
                <textarea
                  rows={4}
                  value={(form.features || []).join("\n")}
                  onChange={(e) => setForm({ ...form, features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                  placeholder={"Publication 1 mois\nVisible dans /evenements\nModération en 48h"}
                  className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-xs font-mono"
                  data-testid="pkg-features-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-wine-500" />
                Actif (visible publiquement sur /evenements/publier)
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2 border-t border-neutral-100 pt-4">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="pkg-save-btn">{editing ? "Mettre à jour" : "Créer"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Edit3Icon() { return <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }

function NotificationRoutesPanel() {
  const [types, setTypes] = useState([]);
  const [routes, setRoutes] = useState({});
  const [fallback, setFallback] = useState("");
  const [globalEmails, setGlobalEmails] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/admin/settings/notification-routes"),
      api.get("/admin/settings/notification-email"),
    ]).then(([r1, r2]) => {
      if (!alive) return;
      setTypes(r1.data?.types || []);
      setRoutes(r1.data?.routes || {});
      setFallback(r1.data?.fallback || "");
      setGlobalEmails(r2.data?.notification_email || "");
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const updateRoute = (typeId, value) => {
    const emails = value.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
    setRoutes({ ...routes, [typeId]: emails });
  };

  const saveRoutes = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings/notification-routes", { routes });
      toast.success("Routage des notifications enregistré");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };

  const saveGlobal = async () => {
    try {
      await api.patch("/admin/settings/notification-email", { notification_email: globalEmails });
      toast.success("Destinataire global mis à jour");
      setFallback(globalEmails);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  if (loading) return <div className="rounded-2xl border border-neutral-100 bg-white p-6 text-sm text-neutral-500"><Loader2Icon /> Chargement…</div>;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-neutral-100 bg-white p-6">
        <h2 className="text-xl font-bold text-navy"><Bell className="mr-2 inline h-5 w-5 text-wine-500" /> Destinataire global (fallback)</h2>
        <p className="mt-1 text-sm text-neutral-600">Email(s) utilisé(s) par défaut quand aucun routage spécifique n&apos;est défini pour un type donné. Séparez par des virgules pour plusieurs destinataires.</p>
        <div className="mt-3 flex gap-2">
          <input value={globalEmails} onChange={(e) => setGlobalEmails(e.target.value)} placeholder="audrey@lawinetech.com, contact@lawinetech.com" className="flex-1 rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="global-notif-email" />
          <button onClick={saveGlobal} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="global-notif-save"><Save className="h-4 w-4" /> Enregistrer</button>
        </div>
      </div>

      <div className="rounded-2xl border border-neutral-100 bg-white p-6">
        <h2 className="text-xl font-bold text-navy">🎯 Routage par type de notification</h2>
        <p className="mt-1 text-sm text-neutral-600">Pour chaque type d&apos;événement, indiquez les destinataires (séparés par des virgules). Laissez vide pour utiliser le destinataire global <strong>{fallback || "(non défini)"}</strong>.</p>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead className="bg-wine-50/40 text-[10px] uppercase text-neutral-500">
              <tr>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Destinataires (emails séparés par virgule)</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => {
                const arr = routes[t.id] || [];
                return (
                  <tr key={t.id} className="border-t border-neutral-100" data-testid={`notif-route-${t.id}`}>
                    <td className="px-3 py-3 align-top">
                      <div className="font-semibold text-navy">{t.label}</div>
                      <div className="text-[10px] font-mono text-neutral-400">{t.id}</div>
                    </td>
                    <td className="px-3 py-3">
                      <textarea
                        rows={Math.max(1, Math.min(3, arr.length))}
                        value={arr.join(", ")}
                        onChange={(e) => updateRoute(t.id, e.target.value)}
                        placeholder={`(par défaut → ${fallback || "audrey@lawinetech.com"})`}
                        className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-xs font-mono"
                        data-testid={`notif-route-input-${t.id}`}
                      />
                      {arr.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {arr.map((em) => (
                            <span key={em} className="rounded-full bg-wine-50 px-2 py-0.5 text-[10px] font-bold text-wine-700">{em}</span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex justify-end">
          <button onClick={saveRoutes} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-6 py-3 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="notif-routes-save">
            <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer le routage"}
          </button>
        </div>
      </div>

      <CrmDigestPanel fallback={fallback} routes={routes} />
    </div>
  );
}

// ============================== CRM WEEKLY DIGEST PANEL ==============================
function CrmDigestPanel({ fallback, routes }) {
  const [stats, setStats] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const recipients = (routes?.prospect_event || []).join(", ") || fallback || "audrey@lawinetech.com";

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/crm-digest/preview");
      setStats(r.data?.stats || null);
      setPreviewHtml(r.data?.html || "");
      setPreviewSubject(r.data?.subject || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur preview");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, []);

  const sendNow = async () => {
    if (!window.confirm(`Envoyer maintenant le digest CRM hebdomadaire à : ${recipients} ?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/crm-digest/tick");
      if (r.data?.ok) toast.success(`📬 Digest envoyé à ${(r.data?.recipients || []).length} destinataire(s)`);
      else toast.error(r.data?.reason || "Échec d'envoi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur envoi");
    } finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="crm-digest-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-navy">📬 Digest CRM hebdomadaire</h2>
          <p className="mt-1 text-sm text-neutral-600">
            Récapitulatif hebdomadaire de l&apos;activité CRM (nouveaux prospects, candidatures, conversions, archives, candidatures en attente d&apos;action).
            Envoyé à : <strong>{recipients}</strong> (configurable via <em>Routage des notifications → Activité CRM</em>).
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            💡 Pour automatiser un envoi hebdomadaire (chaque lundi 9h par exemple), branchez un cron externe sur <code className="rounded bg-neutral-100 px-1">POST /api/admin/crm-digest/tick</code>.
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <button onClick={loadPreview} disabled={loading} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50" data-testid="crm-digest-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </button>
          <button onClick={sendNow} disabled={sending} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid="crm-digest-send">
            {sending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Envoyer maintenant
          </button>
        </div>
      </div>

      {stats && (
        <div className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
          <StatCard label="🆕 Nouveaux prospects (7j)" value={stats.new_prospects} />
          <StatCard label="📝 Nouvelles candidatures (7j)" value={stats.new_candidatures} />
          <StatCard label="✅ Gagnés (7j)" value={stats.won_this_week} highlight="emerald" />
          <StatCard label="❌ Perdus (7j)" value={stats.lost_this_week} highlight="rose" />
          <StatCard label="📦 Archivés (7j)" value={stats.archived_this_week} />
          <StatCard label="⚠️ À relancer (>5j)" value={stats.stale_candidatures} highlight="amber" hint="candidatures sans email récent" />
          <StatCard label="🚨 Comptes orphelins" value={stats.orphan_accounts || 0} highlight={stats.orphan_accounts ? "rose" : null} hint={stats.orphan_accounts ? "Réparer ci-dessous" : "Aucun problème détecté"} />
          <StatCard label="📊 Actifs (total)" value={stats.total_active} />
        </div>
      )}

      {(stats?.orphan_accounts || 0) > 0 && (
        <OrphanRepairBlock onRepaired={loadPreview} />
      )}

      {previewHtml && (
        <details className="mt-4 rounded-xl border border-neutral-100 bg-neutral-50">
          <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-100">
            Aperçu du mail (sujet : {previewSubject})
          </summary>
          <iframe title="crm-digest-preview" srcDoc={previewHtml} className="h-[480px] w-full rounded-b-xl border-t border-neutral-200 bg-white" data-testid="crm-digest-preview-iframe" />
        </details>
      )}
    </div>
  );
}

function StatCard({ label, value, highlight, hint }) {
  const colorMap = {
    emerald: "border-emerald-200 bg-emerald-50",
    rose: "border-rose-200 bg-rose-50",
    amber: "border-amber-200 bg-amber-50",
  };
  return (
    <div className={`rounded-xl border ${highlight ? colorMap[highlight] : "border-neutral-200 bg-white"} p-3`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="mt-0.5 text-2xl font-bold text-navy">{value ?? "—"}</div>
      {hint && <div className="text-[10px] text-neutral-400">{hint}</div>}
    </div>
  );
}

// Sprint 85 ext — Orphan repair block surfaces in the CRM Digest panel when
// orphan_accounts > 0 (member/applicant users with company gap).
function OrphanRepairBlock({ onRepaired }) {
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/crm-digest/orphans");
      setOrphans(r.data?.orphans || []);
    } catch { /* ignored */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const repair = async () => {
    if (!window.confirm(`Réparer ${orphans.length} compte${orphans.length > 1 ? "s" : ""} orphelin${orphans.length > 1 ? "s" : ""} ? (set company_id + promote role→member depuis applicant/event_user)`)) return;
    setRepairing(true);
    try {
      const r = await api.post("/admin/contacts/reconcile-users");
      toast.success(`✅ ${r.data?.repaired || 0} compte(s) réparé(s), ${r.data?.promoted || 0} promu(s) en membre`);
      await load();
      onRepaired?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de réparation");
    } finally { setRepairing(false); }
  };

  return (
    <div className="mt-4 rounded-xl border-2 border-rose-200 bg-rose-50/40 p-4" data-testid="orphan-repair-block">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-rose-900">🚨 Comptes orphelins détectés ({orphans.length})</h3>
          <p className="mt-1 text-xs text-rose-700">
            Ces utilisateurs ont un contact lié à une entreprise mais leur compte n&apos;a pas de <code>company_id</code> (et/ou leur rôle est encore <code>applicant</code>/<code>event_user</code>).
            Réparer rétablit la liaison et leur donne accès à leur Profil entreprise immédiatement.
          </p>
        </div>
        <button onClick={repair} disabled={repairing || loading || orphans.length === 0} className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-5 py-2 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-50" data-testid="orphan-repair-btn">
          {repairing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Réparer maintenant
        </button>
      </div>
      {orphans.length > 0 && (
        <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-rose-100 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-rose-50 text-[10px] uppercase tracking-wider text-rose-800">
              <tr><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Nom</th><th className="px-3 py-2 text-left">Rôle</th><th className="px-3 py-2 text-left">Entreprise à lier</th></tr>
            </thead>
            <tbody>
              {orphans.map((o) => (
                <tr key={o.id} className="border-t border-rose-50">
                  <td className="px-3 py-1.5 font-mono text-[11px] text-neutral-700">{o.email}</td>
                  <td className="px-3 py-1.5">{o.name || "—"}</td>
                  <td className="px-3 py-1.5"><code className="rounded bg-neutral-100 px-1 text-[10px]">{o.role}</code></td>
                  <td className="px-3 py-1.5 font-semibold text-rose-700">{o.linked_company_name || o.linked_company_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Loader2Icon() { return <RefreshCw className="inline h-4 w-4 animate-spin" />; }

// Sprint 88 — Backfill: promote users stuck as 'applicant' despite a paid application
function MemberRoleReconcileBlock() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // Sprint 88b — Single-user diagnostic + surgical promotion
  const [diagEmail, setDiagEmail] = useState("");
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const run = async () => {
    if (!window.confirm("Analyser toutes les candidatures payées + les membres avec company active et promouvoir les utilisateurs bloqués en 'applicant' vers 'member' (idempotent). Continuer ?")) return;
    setLoading(true);
    try {
      const r = await api.post("/admin/applications/reconcile-member-roles");
      setResult(r.data);
      toast.success(`✅ ${r.data.promoted} utilisateur(s) promu(s) au statut membre`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const diagnose = async () => {
    const em = (diagEmail || "").trim().toLowerCase();
    if (!em) { toast.error("Renseigne l'email du membre à diagnostiquer"); return; }
    setDiagLoading(true);
    try {
      const r = await api.get(`/admin/users/diagnose?email=${encodeURIComponent(em)}`);
      setDiag(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setDiagLoading(false);
    }
  };

  const promoteOne = async () => {
    if (!diag?.user?.id) return;
    if (!window.confirm(`Promouvoir ${diag.user.email} en 'member' ?`)) return;
    try {
      await api.post(`/admin/users/${diag.user.id}/promote-to-member`, {
        company_id: diag.user.company_id || diag.contact?.linked_company_id || diag.application?.company_id || null,
      });
      toast.success("✅ Utilisateur promu");
      await diagnose(); // re-run to show the fresh state
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4" data-testid="member-role-reconcile-block">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-sm font-bold text-blue-900">🔧 Réconciliation des rôles membres</div>
          <div className="mt-1 text-xs text-blue-800/80">
            Utile si des candidats sont passés en <strong>paid</strong> ou liés à une <em>Company</em> active mais leurs comptes utilisateurs sont restés en rôle
            <code className="mx-1 rounded bg-white/70 px-1 py-0.5">applicant</code> (n&apos;accèdent pas aux features membres).
            L&apos;action promeut role → member, lie <em>company_id</em> et met à jour le contact CRM associé.
            Idempotent : rejouable à volonté.
          </div>
        </div>
        <button onClick={run} disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                data-testid="reconcile-roles-btn">
          {loading ? <Loader2Icon /> : <RefreshCw className="h-3.5 w-3.5" />}
          Lancer la réconciliation
        </button>
      </div>
      {result && (
        <div className="mt-3 rounded-lg bg-white p-3 text-xs" data-testid="reconcile-result">
          <div><strong>{result.promoted}</strong> promotion{result.promoted > 1 ? "s" : ""} effectuée{result.promoted > 1 ? "s" : ""}.</div>
          {result.skipped && Object.keys(result.skipped).length > 0 && (
            <div className="mt-1 text-neutral-500">
              Ignorés :{" "}
              {Object.entries(result.skipped).map(([k, v]) => <span key={k} className="mr-2">{k} × {v}</span>)}
            </div>
          )}
          {result.details?.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-neutral-600 hover:text-blue-700">Voir les {result.details.length} premières promotions</summary>
              <ul className="mt-1 max-h-56 space-y-0.5 overflow-y-auto pl-4">
                {result.details.map((d, i) => (
                  <li key={`${d.user_id || i}-${d.source || ""}`} className="text-neutral-700">
                    <span className="inline-block w-24 text-[10px] uppercase text-blue-500">{d.source || "app"}</span>
                    <code>{d.email}</code>{d.role_changed && " · role→member"}{d.linked_company && " · +company_id"}{d.contact_id && " · +contact"}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Sprint 88b — Diagnostic + surgical promotion for a single user */}
      <div className="mt-4 border-t border-blue-100 pt-3" data-testid="user-diagnostic">
        <div className="text-[11px] font-bold uppercase text-blue-700">🔍 Diagnostic membre par email</div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="email"
            value={diagEmail}
            onChange={(e) => setDiagEmail(e.target.value)}
            placeholder="email@membre.com"
            className="flex-1 min-w-[220px] rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs"
            data-testid="diagnose-email-input"
          />
          <button onClick={diagnose} disabled={diagLoading} className="rounded-full bg-white border border-blue-300 px-3 py-1.5 text-xs font-bold text-blue-700 hover:bg-blue-50 disabled:opacity-50" data-testid="diagnose-btn">
            {diagLoading ? <Loader2Icon /> : "Diagnostiquer"}
          </button>
        </div>
        {diag && (
          <div className="mt-3 rounded-lg bg-white p-3 text-xs" data-testid="diagnose-result">
            {!diag.found ? (
              <div className="text-rose-700">Aucun compte utilisateur trouvé pour <code>{diag.email}</code>.</div>
            ) : (
              <>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase text-neutral-400">User</div>
                    <div>{diag.user.name} · <code>{diag.user.email}</code></div>
                    <div>Rôle : <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${diag.user.role === "member" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{diag.user.role}</span></div>
                    <div>Company ID : <code>{diag.user.company_id || "—"}</code></div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase text-neutral-400">Company</div>
                    <div>{diag.company?.name || "—"} · <span className="text-neutral-500">{diag.company?.status || "—"}</span></div>
                    <div className="mt-1 text-[10px] font-bold uppercase text-neutral-400">Contact CRM</div>
                    <div>source={diag.contact?.source || "—"} · linked_company_id={diag.contact?.linked_company_id ? "✓" : "—"}</div>
                    <div className="mt-1 text-[10px] font-bold uppercase text-neutral-400">Application</div>
                    <div>{diag.application?.company_name || "—"} · <span className="text-neutral-500">{diag.application?.status || "—"}</span></div>
                  </div>
                </div>
                <div className="mt-2 rounded-md bg-amber-50 p-2">
                  <div className="text-[10px] font-bold uppercase text-amber-700">Diagnostic</div>
                  <ul className="mt-1 space-y-0.5 text-amber-900">
                    {(diag.diagnosis || []).map((d, i) => <li key={i}>• {d}</li>)}
                  </ul>
                </div>
                {diag.user.role !== "member" && (
                  <div className="mt-3 flex justify-end">
                    <button onClick={promoteOne} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-emerald-600" data-testid="promote-user-btn">
                      🚀 Promouvoir ce membre maintenant
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}




// Sprint 92al — Projects panel (WineTech-wide initiatives)
function ProjectsPanel() {
  const [items, setItems] = useState([]);
  const [poles, setPoles] = useState([]);
  const [form, setForm] = useState({ title: "", subtitle: "", description: "", pole: "", image: "", order: 0, status: "active" });

  const load = useCallback(() => {
    api.get("/admin/settings/projects").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/settings/poles").then((r) => setPoles(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    try {
      await api.post("/admin/settings/projects", { ...form, order: items.length + 1 });
      toast.success("Projet créé");
      setForm({ title: "", subtitle: "", description: "", pole: "", image: "", order: 0, status: "active" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur"); }
  };

  const update = async (p, patch) => {
    try { await api.patch(`/admin/settings/projects/${p.id}`, { ...p, ...patch }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Supprimer le projet "${p.title}" ?\nLes membres qui y participent seront délinkés.`)) return;
    try { await api.delete(`/admin/settings/projects/${p.id}`); toast.success("Projet supprimé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur"); }
  };

  const poleOptions = [{ id: "", label: "— Transversal —" }, ...(poles || []).map((p) => ({ id: p.id, label: p.label || p.name || p.id }))];

  return (
    <div data-testid="projects-panel">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2">
            <Rocket className="h-5 w-5 text-wine-500" /> Projets WineTech
          </h2>
          <p className="text-sm text-neutral-600">Programmes et initiatives portés par La WineTech. Les membres peuvent indiquer les projets auxquels ils contribuent. Visible publiquement sur la page <code className="text-xs">/projects</code>.</p>
        </div>
      </div>

      <form onSubmit={create} className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid="projects-create-form">
        <h3 className="text-sm font-bold text-navy">Nouveau projet</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre du projet" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="project-title" />
          <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Sous-titre (1 phrase accroche)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="project-subtitle" />
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description longue — pourquoi, pour qui, ce qui est produit…" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" data-testid="project-description" />
          <select value={form.pole} onChange={(e) => setForm({ ...form, pole: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="project-pole">
            {poleOptions.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="URL image (optionnel)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="project-image" />
        </div>
        <button type="submit" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="project-create">
          <Plus className="h-3.5 w-3.5" /> Créer le projet
        </button>
      </form>

      <div className="mt-6 space-y-3" data-testid="projects-list">
        {items.length === 0 && <p className="text-sm italic text-neutral-500">Aucun projet pour l'instant.</p>}
        {items.map((p) => (
          <div key={p.id} className="rounded-2xl border border-neutral-100 bg-white p-4" data-testid={`project-row-${p.id}`}>
            <div className="flex flex-wrap items-start gap-3">
              {p.image && <img src={p.image} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <input defaultValue={p.title} onBlur={(e) => e.target.value !== p.title && update(p, { title: e.target.value })} className="min-w-[220px] rounded-lg border border-neutral-100 bg-neutral-50/40 px-2 py-1 text-sm font-bold text-navy" data-testid={`project-title-${p.id}`} />
                  <select value={p.pole || ""} onChange={(e) => update(p, { pole: e.target.value })} className="rounded-lg border border-neutral-100 bg-neutral-50/40 px-2 py-1 text-xs" data-testid={`project-pole-${p.id}`}>
                    {poleOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  <code className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] text-neutral-500">/projects/{p.slug}</code>
                </div>
                <input defaultValue={p.subtitle || ""} placeholder="Sous-titre" onBlur={(e) => e.target.value !== (p.subtitle || "") && update(p, { subtitle: e.target.value })} className="mt-2 w-full rounded-lg border border-neutral-100 bg-neutral-50/40 px-2 py-1 text-xs" data-testid={`project-sub-${p.id}`} />
                <textarea defaultValue={p.description || ""} rows={2} placeholder="Description" onBlur={(e) => e.target.value !== (p.description || "") && update(p, { description: e.target.value })} className="mt-2 w-full rounded-lg border border-neutral-100 bg-neutral-50/40 px-2 py-1 text-xs" data-testid={`project-desc-${p.id}`} />
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <input defaultValue={p.image || ""} placeholder="URL image" onBlur={(e) => e.target.value !== (p.image || "") && update(p, { image: e.target.value })} className="flex-1 rounded-lg border border-neutral-100 bg-neutral-50/40 px-2 py-1 text-[11px]" data-testid={`project-img-${p.id}`} />
                  <label className="inline-flex items-center gap-1">
                    <input type="checkbox" checked={p.status === "active"} onChange={(e) => update(p, { status: e.target.checked ? "active" : "archived" })} className="accent-wine-600" data-testid={`project-active-${p.id}`} />
                    Actif
                  </label>
                </div>
              </div>
              <button onClick={() => del(p)} className="grid h-9 w-9 place-items-center rounded-full text-rose-500 hover:bg-rose-50" data-testid={`project-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

